import { z } from 'zod';

import { TypeSystemPage } from '../models/contentful-generated';
import { ContentfulSysSchema } from './contentful-sys.validator';

const SystemPageFieldsSchema: z.ZodType<any> = z.lazy(() =>
  z.looseObject({
    internalName: z.string(),
    pageTitle: z.string(),
    type: z.enum([
      'Addresses',
      'Call Off Order Detail',
      'Checkout',
      'Invoices',
      'My Products and Prices',
      'My Profile',
      'Order Detail',
      'Order Overview',
      'Portal Users',
      'Quote Detail',
      'Registration Numbers',
      'Shipment',
      'Shopping Cart',
      'Vat Numbers',
      'Company Info',
      'Product Compare',
      'Limited Product Detail'
    ]),
    parentPage: SystemPageEntrySchema.optional().nullable(),
    route: z.string().optional().nullable(),
    slug: z.string().optional().nullable(),
    routeIds: z.array(z.string()).optional().nullable(),
    pageContent: z.array(z.any()).optional().nullable(),
    translations: z.record(z.string(), z.any()).optional().nullable()
  })
);

export const SystemPageEntrySchema = z.object({
  sys: ContentfulSysSchema,
  fields: SystemPageFieldsSchema,
  metadata: z
    .object({
      tags: z.array(z.any()).optional().nullable()
    })
    .optional()
    .nullable()
});

export function validateSystemPageEntry(data: TypeSystemPage) {
  return SystemPageEntrySchema.safeParse(data);
}

/**
 * Returns validation result.
 *
 * @param data
 */
export function validateSystemPageEntries(data: TypeSystemPage[]) {
  return z.array(SystemPageEntrySchema).safeParse(data);
}

/**
 * Returns valid entries only. In most cases, this is preferred,
 * It allows us to still show some entries instead of showing nothing when only a single entry is invalid.
 *
 * @param data
 */
export function validateAndFilterSystemPageEntries(data: TypeSystemPage[]) {
  if (!data) return [];

  return data.reduce((acc: TypeSystemPage[], entry) => {
    const validationResult = validateSystemPageEntry(entry);
    if (validationResult.success) {
      acc.push(validationResult.data as unknown as TypeSystemPage);
    } else {
      console.warn(
        `Validation failed for system page entry with id: '${entry?.sys?.id}'. It will be filtered out. Error: ${validationResult.error.message}`
      );
    }
    return acc;
  }, []);
}
